import { api } from '../api/endpoints'
import { isNative } from '../native/native'
import { deleteMeta, metaKeys, setMeta } from '../db/meta'

export type PushSupport = 'supported' | 'unsupported' | 'needs-install'

export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalized)
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

export function pushSupport(): PushSupport {
  const hasApis = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
  if (hasApis) return 'supported'
  return isIos() && !isStandalone() ? 'needs-install' : 'unsupported'
}

export function isIos(): boolean {
  const ua = navigator.userAgent
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

/** Installed: from the Home Screen, or the BOSS Chat app, which is installed by definition (never nudged to Add to Home Screen). */
export function isStandalone(): boolean {
  if (isNative()) return true
  const nav = navigator as Navigator & { standalone?: boolean }
  return nav.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches === true
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  const reg = await navigator.serviceWorker.getRegistration()
  return reg ?? null
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await registration()
  return reg ? reg.pushManager.getSubscription() : null
}

function toBody(subscription: PushSubscription) {
  const json = subscription.toJSON()
  return {
    endpoint: subscription.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
  }
}

export type EnableResult = 'enabled' | 'denied' | 'unsupported' | 'no-worker'

/** Must be called from a user gesture (iOS requires it). */
export async function enablePush(vapidPublicKey: string): Promise<EnableResult> {
  if (pushSupport() !== 'supported') return 'unsupported'
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return 'denied'
  const reg = await navigator.serviceWorker.ready.catch(() => null)
  if (!reg) return 'no-worker'
  let subscription = await reg.pushManager.getSubscription()
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    })
  }
  await api.putPushSubscription(toBody(subscription))
  await setMeta(metaKeys.pushSubscribed, true)
  return 'enabled'
}

export async function disablePush(): Promise<void> {
  const subscription = await currentSubscription()
  if (subscription) {
    const endpoint = subscription.endpoint
    await subscription.unsubscribe().catch(() => false)
    await api.deletePushSubscription(endpoint).catch(() => undefined)
  }
  await deleteMeta(metaKeys.pushSubscribed)
}

/** Unsubscribe locally only (sign out: the server clears subscriptions with the session). */
export async function unsubscribeLocally(): Promise<void> {
  const subscription = await currentSubscription().catch(() => null)
  await subscription?.unsubscribe().catch(() => false)
}

/** Keep the server copy current when permission is already granted. */
export async function refreshPushSubscription(vapidPublicKey: string | null): Promise<void> {
  try {
    if (!vapidPublicKey || pushSupport() !== 'supported' || Notification.permission !== 'granted') return
    const subscription = await currentSubscription()
    if (subscription) await api.putPushSubscription(toBody(subscription))
  } catch {
    // best effort
  }
}
